/* Product and inventory state is rendered by Shopify, not inferred from cached variant JSON. */
(() => {
  if (customElements.get('dervo-product')) return;
  class DervoProduct extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;
      this.addEventListener('click', e => this.onClick(e), { signal });
      this.addEventListener('change', e => this.onChange(e), { signal });
      this.addEventListener('focusout', e => {
        if (e.target.matches('[name="quantity"], [data-sticky-quantity]')) this.syncQuantity(e.target.value);
      }, { signal });
      this.addEventListener('submit', e => {
        if (e.target.matches('.dp-product-form')) this.addToCart(e);
      }, { signal });
      this.addEventListener('keydown', e => this.onKey(e), { signal });
      window.addEventListener('popstate', () => this.refresh(new URL(location.href), false), { signal });
      window.addEventListener('pageshow', e => { if (e.persisted) this.refresh(new URL(location.href), false); }, { signal });
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.mobileQuery.addEventListener('change', () => this.updateStickyLayer(), { signal });
      this.observeBuy();
      this.loadRecommendations();
      if (this.querySelector('[data-feedback-result]') || new URLSearchParams(location.search).has('contact_posted')) this.querySelector('.dp-feedback-dialog')?.showModal();
      this.addEventListener('touchstart', e => { if (!e.target.closest('.dp-stage')) return; this.touchStart = e.changedTouches[0].clientX; }, { passive: true, signal });
      this.addEventListener('touchend', e => {
        if (!e.target.closest('.dp-stage')) return;
        const delta = e.changedTouches[0].clientX - this.touchStart;
        if (Math.abs(delta) > 50) this.stepGallery(delta < 0 ? 1 : -1);
      }, { passive: true, signal });
    }
    disconnectedCallback() { this.lifecycle?.abort(); this.request?.abort(); this.observer?.disconnect(); }
    updateStickyLayer() {
      const bar = this.querySelector('.dp-sticky');
      if (!bar || typeof bar.showPopover !== 'function') return;
      // The browser top layer avoids clipping by theme wrappers on mobile.
      if (this.mobileQuery?.matches) {
        bar.setAttribute('popover', 'manual');
        if (!bar.matches(':popover-open')) bar.showPopover();
      } else if (bar.hasAttribute('popover')) {
        if (bar.matches(':popover-open')) bar.hidePopover();
        bar.removeAttribute('popover');
      }
    }
    observeBuy() {
      this.observer?.disconnect();
      this.updateStickyLayer();
      const anchor = this.querySelector('.dp-buy-card');
      this.observer = new IntersectionObserver(entries => {
        this.classList.toggle('is-sticky', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      });
      if (anchor) this.observer.observe(anchor);
    }
    error(message) {
      const el = this.querySelector('.dp-error');
      el.textContent = message || '';
      el.hidden = !message;
      if (message) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    syncQuantity(value) {
      const main = this.querySelector('[name="quantity"]');
      if (!main) return;
      const min = Number(main.min) || 1, step = Number(main.step) || 1;
      const max = main.hasAttribute('max') ? Number(main.max) : Infinity;
      const number = Number(value);
      let next = Math.max(min, min + Math.floor(((Number.isFinite(number) ? number : min) - min) / step) * step);
      if (max >= min) next = Math.min(next, min + Math.floor((max - min) / step) * step);
      for (const input of this.querySelectorAll('[name="quantity"], [data-sticky-quantity]')) input.value = next;
    }
    onChange(e) {
      if (e.target.matches('[data-option]')) {
        if (this.adding) { this.querySelectorAll('[data-option]').forEach(input => { input.checked = input.closest('.dp-option').classList.contains('is-selected'); }); return; }
        const url = new URL(e.target.dataset.productUrl || this.dataset.url, location.origin);
        if (url.pathname !== new URL(this.dataset.url, location.origin).pathname) { location.assign(url.href); return; }
        url.searchParams.set('option_values', [...this.querySelectorAll('[data-option]:checked')].map(el => el.value).join(','));
        this.refresh(url, true, e.target.value);
      }
      if (e.target.matches('[name="quantity"], [data-sticky-quantity]')) this.syncQuantity(e.target.value);
    }
    async refresh(url, push = false, focusValue = null) {
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      const qty = this.querySelector('[name="quantity"]')?.value || 1;
      this.setAttribute('aria-busy', 'true');
      this.error('');
      url.searchParams.set('section_id', this.dataset.section);
      try {
        const response = await fetch(url, { signal, cache: 'no-store' });
        if (!response.ok) throw new Error('De uitvoering kon niet worden geladen. Probeer het opnieuw.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('dervo-product');
        if (!incoming?.querySelector('[data-dynamic="purchase"]')) throw new Error('De uitvoering kon niet worden geladen. Vernieuw de pagina.');
        if (signal.aborted) return;
        for (const old of this.querySelectorAll('[data-dynamic]')) {
          const fresh = incoming.querySelector(`[data-dynamic="${old.dataset.dynamic}"]`);
          if (fresh) old.replaceWith(fresh);
        }
        this.syncQuantity(qty);
        const purchase = this.querySelector('[data-dynamic="purchase"]');
        if (purchase.dataset.activeMedia) this.showMedia(purchase.dataset.activeMedia);
        const stateUrl = new URL(location.href);
        stateUrl.searchParams.delete('section_id');
        if (purchase.dataset.variant) {
          stateUrl.searchParams.set('variant', purchase.dataset.variant);
          stateUrl.searchParams.delete('option_values');
        } else {
          stateUrl.searchParams.delete('variant');
          stateUrl.searchParams.set('option_values', url.searchParams.get('option_values') || '');
        }
        if (push) history.pushState({}, '', stateUrl);
        if (focusValue) [...this.querySelectorAll('[data-option]')].find(input => input.value === focusValue)?.focus({ preventScroll: true });
        this.observeBuy();
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Revert pending radio state to the last server-confirmed selection.
          this.querySelectorAll('[data-option]').forEach(input => { input.checked = input.closest('.dp-option').classList.contains('is-selected'); });
          this.error(error.message || 'Laden mislukt. Probeer het opnieuw.');
        }
      } finally { if (!signal.aborted) this.removeAttribute('aria-busy'); }
    }
    showMedia(id) {
      const slides = [...this.querySelectorAll('[data-media]')];
      if (!slides.some(slide => slide.dataset.media === String(id))) return;
      for (const slide of slides) {
        slide.hidden = slide.dataset.media !== String(id);
        if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
      }
      this.querySelectorAll('[data-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.thumbnail === String(id))));
      if (this.querySelector('.dp-lightbox').open) this.updateLightbox();
    }
    stepGallery(step) {
      const slides = [...this.querySelectorAll('[data-media]')];
      if (!slides.length) return;
      const current = slides.findIndex(slide => !slide.hidden);
      this.showMedia(slides[(current + step + slides.length) % slides.length].dataset.media);
    }
    updateLightbox() {
      const source = this.querySelector('[data-media]:not([hidden]) img');
      const dialog = this.querySelector('.dp-lightbox');
      if (!source) { dialog.close(); return; }
      const target = dialog.querySelector('img');
      target.src = source.src;
      target.srcset = source.srcset;
      target.sizes = '90vw';
      target.alt = source.alt;
    }
    selectTab(tab) {
      for (const button of this.querySelectorAll('[role="tab"]')) {
        const active = button === tab;
        button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      }
    }
    onKey(e) {
      if (e.target.matches('[role="tab"]') && ['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) {
        e.preventDefault();
        const tabs = [...this.querySelectorAll('[role="tab"]')];
        let index = tabs.indexOf(e.target);
        if (e.key === 'Home') index = 0;
        else if (e.key === 'End') index = tabs.length - 1;
        else index = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        this.selectTab(tabs[index]); tabs[index].focus();
      }
      if (e.target.closest('.dp-lightbox') && ['ArrowLeft','ArrowRight'].includes(e.key)) { e.preventDefault(); this.stepGallery(e.key === 'ArrowRight' ? 1 : -1); }
    }
    onClick(e) {
      const target = e.target.closest('button');
      if (!target) return;
      if (target.hasAttribute('data-quantity')) {
        const input = this.querySelector('[name="quantity"]');
        this.syncQuantity(Number(input.value) + Number(target.dataset.quantity) * (Number(input.step) || 1));
      }
      if (target.hasAttribute('data-thumbnail')) this.showMedia(target.dataset.thumbnail);
      if (target.hasAttribute('data-gallery-step')) this.stepGallery(Number(target.dataset.galleryStep));
      if (target.hasAttribute('data-zoom')) { this.updateLightbox(); this.querySelector('.dp-lightbox').showModal(); }
      if (target.hasAttribute('data-close')) target.closest('dialog')?.close();
      if (target.hasAttribute('data-shipping-open')) this.querySelector('.dp-shipping-dialog').showModal();
      if (target.hasAttribute('data-mobile-panel')) {
        const panel = document.getElementById(target.dataset.mobilePanel);
        const dialog = this.querySelector('.dp-mobile-details');
        dialog.querySelector('[data-mobile-heading]').textContent = target.dataset.panelTitle || target.textContent.trim();
        dialog.querySelector('[data-mobile-content]').innerHTML = panel.innerHTML;
        dialog.querySelectorAll('[data-dynamic]').forEach(node => node.removeAttribute('data-dynamic'));
        dialog.querySelectorAll('[data-mobile-content] [id]').forEach(node => node.removeAttribute('id'));
        dialog.showModal();
      }
      if (target.hasAttribute('data-rating')) {
        this.querySelectorAll('[data-rating]').forEach(button => button.setAttribute('aria-pressed', String(button === target)));
        this.querySelector('[name="contact[Beoordeling]"]').value = target.dataset.rating;
        this.querySelector('[data-feedback-open]').disabled = false;
      }
      if (target.hasAttribute('data-feedback-open')) this.querySelector('.dp-feedback-dialog')?.showModal();
      if (target.matches('[role="tab"]')) this.selectTab(target);
      if (target.hasAttribute('data-details')) { const tab=this.querySelector('[role="tab"]'); if(tab)this.selectTab(tab); this.querySelector('.dp-details').scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      if (target.hasAttribute('data-recommendation-step')) {
        const track = this.querySelector('.dp-recommendation-list');
        track?.scrollBy({ left: Number(target.dataset.recommendationStep) * track.clientWidth, behavior: 'smooth' });
      }
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.adding || this.hasAttribute('aria-busy')) return;
      const form = event.target;
      this.syncQuantity(this.querySelector('[name="quantity"]').value);
      if (!form.reportValidity() || !form.querySelector('[name="id"]:not(:disabled)')?.value) return;
      this.adding = true;
      const buttons = [...this.querySelectorAll('.dp-add')];
      buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy','true'); });
      this.error('');
      let added = false;
      try {
        const drawer = document.querySelector('hdt-cart-drawer');
        const sections = drawer?.sectionsToRender?.map(section => section.id) || ['cart-json'];
        const body = new FormData(form);
        body.set('sections', sections.join(','));
        body.set('sections_url', location.pathname);
        const response = await fetch(`${this.dataset.root.replace(/\/$/, '')}/cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || data.status) {
          document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
          throw new Error(data.description || 'Toevoegen is niet gelukt. Controleer de beschikbaarheid.');
        }
        added = true;
        if (window.dervoOpenCart && await window.dervoOpenCart()) return;
        let cart = {};
        try { cart = JSON.parse(data.sections?.['cart-json']?.split('[split_94]')[1] || '{}'); } catch (_) { /* Cart page fallback below. */ }
        if (drawer && cart.item_count != null && data.sections?.[drawer.sectionId]) {
          this.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'product-form', actionAfterATC: 'open_cart_drawer', variantId: body.get('id'), cartData: { ...data, ...cart }, formData: Object.fromEntries(body.entries()) } }));
        } else location.assign(`${this.dataset.root.replace(/\/$/, '')}/cart`);
      } catch (error) {
        this.error(added ? 'Toegevoegd aan je winkelmand. Open de winkelmand om verder te gaan.' : error.message || 'Verbinding onderbroken. Controleer je winkelmand voordat je opnieuw probeert.');
      } finally {
        this.adding = false;
        buttons.forEach(button => { button.disabled = !form.querySelector('[name="id"]:not(:disabled)'); button.removeAttribute('aria-busy'); });
      }
    }
    async loadRecommendations() {
      const host = this.querySelector('[data-recommendations-url]');
      if (!host || host.querySelector('[data-recommendation-content]')) return;
      try {
        const response = await fetch(host.dataset.recommendationsUrl, { signal: this.lifecycle.signal });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-recommendation-content]');
        if (content) host.replaceChildren(content);
      } catch (_) { /* Optional recommendations should never block purchasing. */ }
    }
  }
  customElements.define('dervo-product', DervoProduct);
})();
